import {Box, Button, Divider, InputAdornment, Stack, TextField, ToggleButtonGroup, Typography} from "@mui/material";
import {Navigate, useNavigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {
    StyledAlphaFilterToggleButton,
    StyledInputBase,
    StyledSearch,
    StyledSearchIconWrapper
} from "../../AppStyledComponents.tsx";
import {useMemo, useState} from "react";

interface SampleResourceDefinition {
    key: string;
    display: string;
    total: number;
}

// TODO: Replace with ODIExtensions.getResourceDefinitions() + profileCounts.getTypeCounts(resourceType)
const DEFINITIONS_BY_TYPE: Record<string, SampleResourceDefinition[]> = {
    files: [
        { key: "archive", display: "Archive", total: 4 },
        { key: "audio", display: "Audio", total: 12 },
        { key: "blueprint", display: "Blueprint", total: 2 },
        { key: "code", display: "Code", total: 38 },
        { key: "contract", display: "Contract", total: 6 },
        { key: "database", display: "Database Dump", total: 3 },
        { key: "document", display: "Document", total: 24 },
        { key: "ebook", display: "Ebook", total: 7 },
        { key: "email", display: "Email", total: 45 },
        { key: "font", display: "Font", total: 1 },
        { key: "image", display: "Image", total: 56 },
        { key: "invoice", display: "Invoice", total: 11 },
        { key: "json", display: "JSON", total: 19 },
        { key: "log", display: "Log", total: 28 },
        { key: "markdown", display: "Markdown", total: 9 },
        { key: "notebook", display: "Notebook", total: 4 },
        { key: "pdf", display: "PDF", total: 42 },
        { key: "presentation", display: "Presentation", total: 8 },
        { key: "receipt", display: "Receipt", total: 15 },
        { key: "script", display: "Script", total: 13 },
        { key: "spreadsheet", display: "Spreadsheet", total: 22 },
        { key: "text", display: "Text", total: 31 },
        { key: "video", display: "Video", total: 18 },
        { key: "xml", display: "XML", total: 5 },
        { key: "yaml", display: "YAML", total: 3 },
        { key: "zip", display: "Zip", total: 7 },
        { key: "1password-export", display: "1Password Export", total: 1 },
    ],
    pages: [
        { key: "article", display: "Article", total: 34 },
        { key: "blog", display: "Blog Post", total: 21 },
        { key: "catalog", display: "Catalog", total: 4 },
        { key: "dashboard", display: "Dashboard", total: 2 },
        { key: "documentation", display: "Documentation", total: 17 },
        { key: "event", display: "Event", total: 9 },
        { key: "faq", display: "FAQ", total: 6 },
        { key: "feed", display: "Feed", total: 11 },
        { key: "forum", display: "Forum Thread", total: 28 },
        { key: "gallery", display: "Gallery", total: 5 },
        { key: "knowledge", display: "Knowledge Base", total: 13 },
        { key: "landing", display: "Landing", total: 8 },
        { key: "news", display: "News", total: 42 },
        { key: "press", display: "Press Release", total: 7 },
        { key: "profile", display: "Profile", total: 19 },
        { key: "recipe", display: "Recipe", total: 3 },
        { key: "signup", display: "Signup", total: 2 },
        { key: "story", display: "Story", total: 14 },
        { key: "topic", display: "Topic", total: 10 },
        { key: "video", display: "Video", total: 22 },
        { key: "wiki", display: "Wiki", total: 16 },
        { key: "#404", display: "#404 Snapshot", total: 1 },
    ],
    records: [
        { key: "audit", display: "Audit Record", total: 18 },
        { key: "budget", display: "Budget", total: 5 },
        { key: "crm", display: "CRM Entry", total: 23 },
        { key: "database", display: "Database Row", total: 61 },
        { key: "export", display: "Export", total: 7 },
        { key: "form", display: "Form Submission", total: 14 },
        { key: "historical", display: "Historical Record", total: 9 },
        { key: "invoice", display: "Invoice Record", total: 12 },
        { key: "journal", display: "Journal Entry", total: 20 },
        { key: "kpi", display: "KPI Snapshot", total: 3 },
        { key: "ledger", display: "Ledger Entry", total: 35 },
        { key: "manifest", display: "Manifest", total: 4 },
        { key: "node", display: "Node Metric", total: 8 },
        { key: "order", display: "Order", total: 27 },
        { key: "payment", display: "Payment", total: 16 },
        { key: "query", display: "Query Result", total: 6 },
        { key: "receipt", display: "Receipt Record", total: 11 },
        { key: "schema", display: "Schema", total: 2 },
        { key: "transfer", display: "Transfer", total: 19 },
        { key: "warehouse", display: "Warehouse Entry", total: 8 },
        { key: "zone", display: "Zone Log", total: 3 },
    ],
    enrichments: [
        { key: "annotation", display: "Annotation", total: 6 },
        { key: "biometric", display: "Biometric Match", total: 2 },
        { key: "classification", display: "Classification", total: 14 },
        { key: "detection", display: "Detection", total: 9 },
        { key: "faces", display: "Face Recognition", total: 11 },
        { key: "geocode", display: "Geocode", total: 17 },
        { key: "hash", display: "Hash Match", total: 4 },
        { key: "identity", display: "Identity Resolution", total: 7 },
        { key: "keyword", display: "Keyword Tag", total: 22 },
        { key: "language", display: "Language Detection", total: 8 },
        { key: "metadata", display: "Metadata Enrichment", total: 19 },
        { key: "nlp", display: "NLP Entities", total: 13 },
        { key: "ocr", display: "OCR Text", total: 31 },
        { key: "pattern", display: "Pattern Match", total: 5 },
        { key: "quality", display: "Quality Score", total: 3 },
        { key: "sentiment", display: "Sentiment", total: 12 },
        { key: "tag", display: "Auto Tag", total: 25 },
        { key: "translation", display: "Translation", total: 10 },
        { key: "verification", display: "Verification", total: 6 },
    ],
};

const TYPE_PLURAL: Record<string, string> = {
    files: "Files",
    pages: "Pages",
    records: "Records",
    enrichments: "Enrichments",
};

function resourceIconFor(typeKey: string): string {
    // Desktop uses SessionImages.defaultImage(resourceType) — currently the type icon.
    // TODO: Swap to definition-specific icons when /images/resource/definitions/* are introduced.
    return `/images/resource_${typeKey}.svg`;
}

function titleCase(raw: string): string {
    if (!raw) return "";
    return raw
        .split(/[._-]+/)
        .map((part) => part.length === 0 ? "" : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
        .trim();
}

function firstCharBucket(display: string): string {
    const c = display.trim().charAt(0).toUpperCase();
    if (c >= "A" && c <= "Z") return c;
    return "!#1";
}

function UXResourceSelect() {
    const { networkUID, typeKey } = useParams();
    const navigate = useNavigate();

    const LEFT_1 = "ABCDEFGHIJKLM".split("");
    const LEFT_2 = "NOPQRSTUVWXYZ".split("");
    const SPAN = "!#1";

    const normalizedType = String(typeKey ?? "").toLowerCase();
    const friendlyType = useMemo(
        () => TYPE_PLURAL[normalizedType] ?? titleCase(normalizedType),
        [normalizedType]
    );

    const [activeKey, setActiveKey] = useState<string>("A");
    const [datasetSearch, setDatasetSearch] = useState<string>("");
    const [resultsFilter, setResultsFilter] = useState<string>("");

    const definitions = useMemo<SampleResourceDefinition[]>(
        () => DEFINITIONS_BY_TYPE[normalizedType] ?? [],
        [normalizedType]
    );

    const counts = useMemo(() => {
        const map: Record<string, number> = {};
        LEFT_1.concat(LEFT_2).forEach((l) => { map[l] = 0; });
        map[SPAN] = 0;
        definitions.forEach((d) => {
            const bucket = firstCharBucket(d.display);
            map[bucket] = (map[bucket] ?? 0) + 1;
        });
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definitions]);

    const visible = useMemo(() => {
        const datasetQ = datasetSearch.trim().toLowerCase();
        const base = datasetQ.length > 0
            ? definitions.filter((d) => d.display.toLowerCase().includes(datasetQ))
            : definitions.filter((d) => firstCharBucket(d.display) === activeKey);

        const resultQ = resultsFilter.trim().toLowerCase();
        return resultQ.length > 0
            ? base.filter((d) => d.display.toLowerCase().includes(resultQ))
            : base;
    }, [definitions, datasetSearch, activeKey, resultsFilter]);

    const filteredCount = visible.length;

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%" >
                    <WidgetHeader
                        title={`Resources: ${friendlyType}`}
                        breadcrumbs={[
                            {
                                display: "Networks",
                                linkTo: `/networks/`,
                                activeView: false
                            },
                            {
                                display: String(networkUID),
                                linkTo: `/networks/${networkUID}`,
                                activeView: false
                            },
                            {
                                display: "Resources",
                                linkTo: `/networks/${networkUID}`,
                                activeView: false
                            },
                            {
                                display: friendlyType,
                                linkTo: "",
                                activeView: true
                            }]}
                    />
                    <Stack direction="row" spacing={2} width="100%" alignItems="stretch">
                        <Stack sx={appStyles.alphaNumberFilterLeftRail}>
                            <Stack direction="column" alignItems="center" spacing={0}>
                                <StyledSearch>
                                    <StyledSearchIconWrapper>
                                        <img src="/images/search_white.svg" width={16} />
                                    </StyledSearchIconWrapper>
                                    <StyledInputBase
                                        placeholder="Search"
                                        value={datasetSearch}
                                        onChange={(e) => setDatasetSearch(e.target.value)}
                                        inputProps={{ "aria-label": "search" }}
                                    />
                                </StyledSearch>
                                <Stack sx={appStyles.alphaNumberDatasetSearchDivider}></Stack>
                                <Stack direction="row" spacing={1} sx={appStyles.alphaNumberFilterAlphaRailRow}>
                                    <ToggleButtonGroup
                                        orientation="vertical"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupVertical}
                                    >
                                        {LEFT_1.map((k) => (
                                            <StyledAlphaFilterToggleButton
                                                key={k}
                                                value={k}
                                                sx={appStyles.alphaNumberToggleButton}
                                            >
                                                <span className="rv-pill">
                                                    <span className="rv-label">{k}</span>
                                                    <span className="rv-count">{counts[k] ?? 0}</span>
                                                </span>
                                            </StyledAlphaFilterToggleButton>
                                        ))}
                                    </ToggleButtonGroup>

                                    <ToggleButtonGroup
                                        orientation="vertical"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupVertical}
                                    >
                                        {LEFT_2.map((k) => (
                                            <StyledAlphaFilterToggleButton
                                                key={k}
                                                value={k}
                                                sx={appStyles.alphaNumberToggleButton}
                                            >
                                                <span className="rv-pill">
                                                    <span className="rv-label">{k}</span>
                                                    <span className="rv-count">{counts[k] ?? 0}</span>
                                                </span>
                                            </StyledAlphaFilterToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Stack>

                                <Stack sx={appStyles.alphaNumberFooterContainer}>
                                    <ToggleButtonGroup
                                        orientation="horizontal"
                                        value={activeKey}
                                        exclusive
                                        onChange={(_, v) => v && setActiveKey(v)}
                                        sx={appStyles.alphaNumberToggleGroupSpan}
                                    >
                                        <StyledAlphaFilterToggleButton
                                            value={SPAN}
                                            sx={[
                                                appStyles.alphaNumberToggleButton,
                                                appStyles.alphaNumberToggleButtonSpan,
                                                appStyles.alphaNumberToggleButtonFooter,
                                            ]}
                                        >
                                            <span className="rv-pill">
                                                <span className="rv-label">{SPAN}</span>
                                                <span className="rv-count">{counts[SPAN] ?? 0}</span>
                                            </span>
                                        </StyledAlphaFilterToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Stack>
                        </Stack>

                        <Stack spacing={1} sx={appStyles.alphaNumberCenter}>
                            <Stack sx={appStyles.alphaNumberResultsPanel} direction="column">
                                <Typography sx={{...appStyles.panelSectionTitle, padding: "8px 0 12px"}}>
                                    {friendlyType}
                                </Typography>

                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" sx={{width: "100%", padding: "0 12px 10px", rowGap: 1}}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => navigate(`/networks/${networkUID}/importers`)}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Import
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            // TODO: Replace with bulk on-demand enrichment API call (desktop: System.out.println stub)
                                            console.log("Running bulk on-demand enrichment process");
                                        }}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Enrich
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => {
                                            // TODO: Replace with bulk on-demand research API call (desktop: System.out.println stub)
                                            console.log("Running bulk on-demand research process");
                                        }}
                                        sx={appStyles.panelActionButton}
                                    >
                                        Research
                                    </Button>
                                </Stack>

                                <Divider flexItem sx={{width: "100%", borderColor: "#3F464F", marginBottom: "10px"}} />

                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{padding: "0 12px 6px"}}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{width: 350}}>
                                        <TextField
                                            size="small"
                                            placeholder="Filter results"
                                            value={resultsFilter}
                                            onChange={(e) => setResultsFilter(e.target.value)}
                                            sx={appStyles.alphaNumberFilterField}
                                            fullWidth
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/images/search_white.svg"
                                                            height={16}
                                                            style={{ opacity: 0.75 }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Stack>
                                    <Typography sx={appStyles.alphaNumberFilterCount}>
                                        {filteredCount} results
                                    </Typography>
                                </Stack>

                                {definitions.length === 0 ? (
                                    <Typography sx={{color: "#a8b8c0", fontSize: "0.8rem", textAlign: "center", padding: "24px 0"}}>
                                        No definitions configured for this resource type.
                                    </Typography>
                                ) : visible.length === 0 ? (
                                    <Typography sx={{color: "#a8b8c0", fontSize: "0.8rem", textAlign: "center", padding: "24px 0"}}>
                                        No definitions match the current filter.
                                    </Typography>
                                ) : (
                                    <Box sx={{display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", padding: "8px 16px 16px"}}>
                                        {visible.map((d) => (
                                            <ResourceDefinitionCard
                                                key={d.key}
                                                definition={d}
                                                friendlyType={friendlyType}
                                                iconSrc={resourceIconFor(normalizedType)}
                                                onSelect={() => navigate(
                                                    `/networks/${networkUID}/resources/${normalizedType}/${d.key}`,
                                                    { state: { definition: { ...d, type: normalizedType, friendlyType } } }
                                                )}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

const resourceDefinitionCardSx = {
    background: "linear-gradient(135deg, rgba(9,25,32,0.85) 0%, rgba(20,40,55,0.7) 100%)",
    border: "1px solid #3F464F",
    borderRadius: "12px",
    padding: "12px 16px",
    width: 360,
    minWidth: 300,
    cursor: "pointer",
    transition: "border-color 0.15s, background-color 0.15s",
    "&:hover": {
        borderColor: "rgba(2, 207, 238, 0.45)",
        backgroundColor: "rgba(2, 207, 238, 0.04)",
    },
};

function ResourceDefinitionCard({definition, friendlyType, iconSrc, onSelect}: {
    definition: SampleResourceDefinition;
    friendlyType: string;
    iconSrc: string;
    onSelect: () => void;
}) {
    return (
        <Box sx={resourceDefinitionCardSx} onClick={onSelect}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{
                    width: 40, height: 40, borderRadius: "8px", flexShrink: 0,
                    backgroundColor: "rgba(2, 207, 238, 0.12)",
                    border: "1px solid rgba(2, 207, 238, 0.25)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    overflow: "hidden",
                }}>
                    <Box
                        component="img"
                        src={iconSrc}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = "/images/resource_files.svg"; }}
                        sx={{width: 24, height: 24, opacity: 0.85}}
                    />
                </Box>
                <Stack spacing={0.3} sx={{minWidth: 0, flex: 1}}>
                    <Typography sx={{fontSize: "0.85rem", fontWeight: 600, color: "#F2F5FA", textAlign: "left"}} noWrap>
                        {definition.display}
                    </Typography>
                    <Typography sx={{fontSize: "0.7rem", fontWeight: 300, color: "rgba(242, 245, 250, 0.7)", textAlign: "left"}} noWrap>
                        {`Resource: ${friendlyType}`}
                    </Typography>
                </Stack>
                <Stack alignItems="flex-end" sx={{flexShrink: 0, minWidth: 48}}>
                    <Typography sx={{fontSize: "1.1rem", fontWeight: 600, color: "#02cfee", lineHeight: 1}}>
                        {definition.total}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
}

export default UXResourceSelect;
